import { EventsFromTopLeaguesBySportIdRequest, EventsBySportIdRequest } from "sb-resp-lib";
import { DateRangeParam, CardConfiguration, MarketConfigType, CardItem, FavoriteItem } from "../../typings";
import { EventListPresenterRequest } from "../../contracts";
import { MarketTypeRequest, Pagination } from "sbtech-online-sportcontent-sportbook";
import { compareArrays } from "../compareArrays";

export const bffRequestBuilder = {
    buildSportIdsUpdate(params: BFFRequestParams, sportIds: string[]) {
        this.buildIds(params.request, sportIds);
        this.buildEventTags(params.request);
        this.buildMarketTypeRequests(params, sportIds);
    },

    buildDateRangeUpdate(params: BFFRequestParams, dateRange: DateRangeParam) {
        this.buildDateRange(params.request, dateRange);
        this.buildEventTags(params.request);
        this.buildMarketTypeRequests(params, params.request.ids);
    },

    buildLeagueIdsUpdate(params: BFFRequestParams, leagueIds: string[], sportIds: string[]) {
        this.buildIds(params.request, leagueIds);
        this.buildMarketTypeRequests(params, sportIds);
    },

    buildEventTagsUpdate(params: BFFRequestParams, eventTags: EventsBySportIdRequest.EventTagsEnum[]) {
        this.buildIds(params.request, params.allSportIds);
        this.buildEventTags(params.request, eventTags);
        this.buildMarketTypeRequests(params);
    },

    buildEventIdsUpdate(params: BFFRequestParams, favoriteEvents: FavoriteItem[]) {
        this.buildIds(params.request, favoriteEvents.map(ev => ev.eventId));
        this.buildMarketTypeRequests(params, favoriteEvents.map(ev => ev.sportId).filter((id, i, array) => array.indexOf(id) === i));
    },

    buildSportIdRequest(request: EventsFromTopLeaguesBySportIdRequest, pagination: Pagination): EventsBySportIdRequest {
        const result = <EventsBySportIdRequest> { ...request };
        this.buildExcludeRegionIds(result, request.regionIds);
        this.buildRegionIds(result, undefined);
        this.buildLeagueState(result, EventsBySportIdRequest.LeagueStateEnum.Regular);
        this.buildPagination(result, pagination);
        return result;
    },

    buildPagination(request: EventListPresenterRequest, pagination: Pagination) {
        request.pagination = pagination;
    },

    buildLeagueState(request: EventListPresenterRequest, leagueState: EventsBySportIdRequest.LeagueStateEnum) {
        request.leagueState = leagueState;
    },

    buildExcludeRegionIds(request: EventsBySportIdRequest, regionIds: string[]) {
        request.excludeRegionIds = regionIds;
    },

    buildRegionIds(request: EventListPresenterRequest, regionIds: string[] | undefined) {
        request.regionIds = regionIds;
    },

    buildIds(request: EventListPresenterRequest, ids: string[]) {
        request.ids = ids;
    },

    buildDateRange(request: EventsFromTopLeaguesBySportIdRequest, dateRange: DateRangeParam) {
        request.dateRange = dateRange;
    },

    buildEventTags(request: EventsFromTopLeaguesBySportIdRequest, eventTags: EventsBySportIdRequest.EventTagsEnum[] = []) {
        request.eventTags = eventTags;
    },

    buildMarketTypeRequests(params: BFFRequestParams, sportIds: string[] = []) {
        const marketTypeRequests: MarketTypeRequest[] = [];
        const defaultMarkets = this.buildMarketIds(
            params.request.eventState, this.buildEventMarkets(params.cardConfiguration["0"], params.request.eventTypes || []));
        const defaultConfiguration = {
            sportIds: <string[]> [],
            marketTypeIds: defaultMarkets,
            statement: MarketTypeRequest.StatementEnum.Include
        };
        let sportIdsToExclude: string[] = [];

        if (!sportIds.length) {
            sportIds = Object.keys(params.cardConfiguration).filter(k => k !== "0");
            sportIdsToExclude = Object.keys(params.cardConfiguration).filter(k => k !== "0");
        }

        sportIds.forEach(sportId => {
            const config = params.cardConfiguration[sportId];
            if (!config) {
                defaultConfiguration.sportIds.push(sportId);
            } else {
                const marketTypeIds = this.buildMarketIds(params.request.eventState, this.buildEventMarkets(config, params.request.eventTypes || []));
                if (sportIdsToExclude.length && compareArrays(marketTypeIds, defaultMarkets)) {
                    var index = sportIdsToExclude.indexOf(sportId);
                    index > -1 && sportIdsToExclude.splice(index, 1);
                } else {
                    marketTypeRequests.push({
                        sportIds: [sportId],
                        marketTypeIds,
                        statement: MarketTypeRequest.StatementEnum.Include
                    });
                }
            }
        });

        params.request.marketTypeRequests = marketTypeRequests;
        sportIdsToExclude.length && params.request.marketTypeRequests.push({
            sportIds: sportIdsToExclude,
            marketTypeIds: defaultConfiguration.marketTypeIds,
            statement: MarketTypeRequest.StatementEnum.Exclude
        });

        if (defaultConfiguration.sportIds.length || !params.request.marketTypeRequests.length) {
            defaultConfiguration.sportIds = this.buildDefaultSportIds(defaultConfiguration.sportIds, sportIds);
            params.request.marketTypeRequests.push(defaultConfiguration);
        }

        this.buildDefaultOutrightMarkets(params);
    },

    buildMarketIds(eventState: EventsFromTopLeaguesBySportIdRequest.EventStateEnum, markets: MarketConfigType[]): string[] {
        return !eventState || eventState === EventsFromTopLeaguesBySportIdRequest.EventStateEnum.Mixed
            ? markets.map(m => m.idLive).concat(markets.map(m => m.idPreLive)).filter((value, index, self) => self.indexOf(value) === index)
            : markets.map(m => m[`id${eventState}`]);
    },

    buildEventMarkets(cardItem: CardItem, eventTypes: EventsBySportIdRequest.EventTypesEnum[]): MarketConfigType[] {
        const eventTypesCheck = this.buildEventTypes(eventTypes);

        if (eventTypesCheck.isFixtureEventType) {
            return cardItem.markets;
        } else if (eventTypesCheck.isOutrightEventType) {
            return cardItem.outrightMarkets;
        }

        return cardItem.markets;
    },

    buildDefaultOutrightMarkets(params: BFFRequestParams) {
        const eventTypesCheck = this.buildEventTypes(params.request.eventTypes || []);

        if (eventTypesCheck.isFixtureEventType && eventTypesCheck.isOutrightEventType) {
            params.request.marketTypeRequests.push({
                sportIds: [],
                marketTypeIds: this.buildMarketIds(params.request.eventState, params.cardConfiguration["0"].outrightMarkets),
                statement: MarketTypeRequest.StatementEnum.Include
            });
        }
    },

    buildEventTypes(eventTypes: EventsBySportIdRequest.EventTypesEnum[]): EventTypesCheck {
        return <EventTypesCheck>{
            isFixtureEventType: eventTypes.indexOf(EventsBySportIdRequest.EventTypesEnum.Fixture) > -1,
            isOutrightEventType: eventTypes.indexOf(EventsBySportIdRequest.EventTypesEnum.Outright) > -1
        };
    },

    buildDefaultSportIds(defaultIds: string[], sportIds: string[]): string[] {
        return defaultIds.length === sportIds.length ? [] : defaultIds;
    }
}

export type BFFRequestParams = {
    request: EventListPresenterRequest,
    cardConfiguration: CardConfiguration,
    allSportIds: string[]
}

export type EventTypesCheck = {
    isFixtureEventType: boolean,
    isOutrightEventType: boolean
}
